import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, Upload, Loader2, ShieldAlert, FileSpreadsheet, CheckCircle2, XCircle, Palette, Play, AlertCircle, DollarSign } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import useAuthStore from "@/store/authStore";
import MergeAuditHealthTile from "@/components/MergeAuditHealthTile";
import PantoneReconcilerTile from "@/components/PantoneReconcilerTile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin → Database Updates
 *
 * One-shot baseline data refreshes that sit outside the normal operational
 * upload flows. Today: only "RM Prices" — bulk-update the current price per
 * (Vendor ID, RM ID) in `rm_prices_history` so the existing 3-month-average
 * and last-invoice consumer logic naturally picks it up.
 *
 * Each tile is self-contained — adding a new bulk-update capability (e.g.
 * BOM cost refresh, vendor lead-time refresh) is just another `<UpdateTile>`
 * with its own template/upload endpoint pair.
 */
const UpdateTile = ({
  title,
  description,
  mandatoryColumns,
  optionalColumns,
  templateEndpoint,
  uploadEndpoint,
  templateFilename,
  testid,
  /** Optional: if true, render an RM-category dropdown and require selection
   *  before download/upload. The picked category code is appended as
   *  ?category=<CODE> on template GET and as a `category` form field on upload. */
  categoryPicker = false,
}) => {
  const fileInputRef = useRef(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const token = useAuthStore((s) => s.token);

  useEffect(() => {
    if (!categoryPicker) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/rm-categories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // The endpoint returns EITHER an array of {code, name, ...} or an
        // object keyed by category code → normalise to an array and sort.
        const raw = res.data;
        let list = [];
        if (Array.isArray(raw)) {
          list = raw;
        } else if (raw && typeof raw === "object") {
          list = Object.entries(raw).map(([code, val]) => ({
            code: val?.code || code,
            name: val?.name || code,
            ...val,
          }));
        }
        list = list
          .filter((c) => c && c.code && c.is_active !== false)
          .sort((a, b) => (a.code || "").localeCompare(b.code || ""));
        setCategories(list);
      } catch (_e) {
        toast.error("Failed to load RM categories");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryPicker]);

  const downloadTemplate = async () => {
    if (categoryPicker && !selectedCategory) {
      toast.error("Pick a category before downloading the template");
      return;
    }
    setLoadingTemplate(true);
    try {
      const url = categoryPicker
        ? `${API}${templateEndpoint}?category=${encodeURIComponent(selectedCategory)}`
        : `${API}${templateEndpoint}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = categoryPicker
        ? templateFilename.replace(/\.xlsx$/i, `_${selectedCategory}.xlsx`)
        : templateFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("Template downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to download template");
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-upload of same filename
    if (!file) return;
    if (categoryPicker && !selectedCategory) {
      toast.error("Pick a category before uploading");
      return;
    }
    setUploading(true);
    setLastResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (categoryPicker && selectedCategory) {
        fd.append("category", selectedCategory);
      }
      const res = await axios.post(`${API}${uploadEndpoint}`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        responseType: "blob",
      });
      // Pull the line-wise summary out of the response headers — the body
      // is the Excel receipt itself, so we surface the totals separately.
      const total = parseInt(res.headers["x-upload-total"] || "0", 10);
      const success = parseInt(res.headers["x-upload-success"] || "0", 10);
      const errors = parseInt(res.headers["x-upload-errors"] || "0", 10);
      const skipped = parseInt(res.headers["x-upload-skipped"] || "0", 10);
      setLastResult({ total, success, errors, skipped, filename: file.name });

      // Auto-download the receipt
      const disp = res.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(disp);
      const outName = m ? m[1] : `upload_receipt_${Date.now()}.xlsx`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = outName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      if (errors > 0 && success === 0) {
        toast.error(`${errors} error(s) — none persisted. See downloaded receipt.`, { duration: 8000 });
      } else if (errors > 0) {
        toast.warning(`${success} row(s) saved, ${errors} failed. See downloaded receipt.`, { duration: 8000 });
      } else {
        toast.success(`${success} row(s) saved. Receipt downloaded.`);
      }
    } catch (err) {
      // Error responses with json body come back as a Blob — decode it.
      let detail = "Upload failed";
      try {
        if (err?.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const obj = JSON.parse(text);
          detail = obj.detail || detail;
        } else {
          detail = err?.response?.data?.detail || err.message || detail;
        }
      } catch {
        detail = err.message || detail;
      }
      toast.error(detail, { duration: 8000 });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card data-testid={testid}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-orange-600" />
              {title}
            </CardTitle>
            <CardDescription className="mt-1 text-xs">{description}</CardDescription>
          </div>
          <Badge variant="outline" className="text-[10px] uppercase border-rose-300 text-rose-700">
            One-shot
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-xs space-y-1">
          <div>
            <span className="font-semibold text-zinc-700">Mandatory:</span>{" "}
            <span className="font-mono text-zinc-600">{mandatoryColumns.join(", ")}</span>
          </div>
          {optionalColumns?.length > 0 && (
            <div>
              <span className="font-semibold text-zinc-700">Optional:</span>{" "}
              <span className="font-mono text-zinc-500">{optionalColumns.join(", ")}</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {categoryPicker && (
            <div className="w-full mb-2">
              <Label className="text-xs text-zinc-600 mb-1 block">
                Pick category to scope the template & validate the upload
              </Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger
                  className="w-full md:w-72"
                  data-testid={`${testid}-category-select`}
                >
                  <SelectValue placeholder="— Select a category —" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      <span className="font-mono">{c.code}</span>
                      <span className="text-zinc-500"> — {c.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={downloadTemplate}
            disabled={loadingTemplate || (categoryPicker && !selectedCategory)}
            data-testid={`${testid}-template-btn`}
          >
            {loadingTemplate ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download Template
          </Button>
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || (categoryPicker && !selectedCategory)}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid={`${testid}-upload-btn`}
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {uploading ? "Processing…" : "Upload File"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={handleFileChange}
            data-testid={`${testid}-file-input`}
          />
        </div>

        {lastResult && (
          <div
            className="text-xs border rounded-md p-3 bg-zinc-50 space-y-1"
            data-testid={`${testid}-last-result`}
          >
            <div className="font-medium text-zinc-700 truncate">{lastResult.filename}</div>
            <div className="flex flex-wrap gap-3 mt-1">
              <span className="flex items-center gap-1 text-emerald-700">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {lastResult.success} success
              </span>
              <span className="flex items-center gap-1 text-rose-700">
                <XCircle className="w-3.5 h-3.5" />
                {lastResult.errors} errors
              </span>
              {lastResult.skipped > 0 && (
                <span className="text-amber-700">{lastResult.skipped} skipped</span>
              )}
              <span className="text-zinc-500">of {lastResult.total} row(s)</span>
            </div>
            <div className="text-zinc-500 text-[11px]">
              Receipt with per-row reasons was downloaded automatically.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PantoneMigrationTile = () => {
  const token = useAuthStore((s) => s.token);
  const [dryRunning, setDryRunning] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);
  const [resultMode, setResultMode] = useState(null);
  const [showCommitConfirm, setShowCommitConfirm] = useState(false);

  const run = async (dryRun) => {
    const setBusy = dryRun ? setDryRunning : setCommitting;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/colourway/admin/migrate-mb-pantone?dry_run=${dryRun ? "true" : "false"}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      setResultMode(dryRun ? "dry_run" : "commit");
      if (dryRun) {
        toast.success(`Scan complete — ${res.data.migrated} ready to tag, ${res.data.invalid_candidates} need attention, ${res.data.still_missing} have no match`);
      } else {
        toast.success(`Migration committed — ${res.data.migrated} MB / PWD rows tagged`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Migration failed", { duration: 8000 });
    } finally {
      setBusy(false);
      setShowCommitConfirm(false);
    }
  };

  const downloadPlan = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/colourway/admin/migrate-mb-pantone/export`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );
      const disp = res.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(disp);
      const outName = m ? m[1] : `mb_pantone_migration_plan_${Date.now()}.xlsx`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = outName;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Plan downloaded — ${res.headers["x-proposed"] || "?"} proposed / ${res.headers["x-invalid"] || "?"} invalid / ${res.headers["x-missing"] || "?"} missing`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card data-testid="pantone-migration-tile">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Palette className="w-5 h-5 text-rose-600" />
              MB / PWD Pantone Migration (data-side)
            </CardTitle>
            <CardDescription className="mt-1 text-xs">
              Scans every untagged MB / PWD row and looks for the Pantone code
              under alt category_data fields (<code className="font-mono">pantone_code</code>,
              <code className="font-mono"> pantone_shade</code>, <code className="font-mono">color_code</code>, etc.)
              plus the description text. Validates against the Pantone master
              and copies into <code className="font-mono">category_data.pantone</code>.
              No manual tagging when the data is already in the system.
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-[10px] uppercase border-rose-300 text-rose-700">
            One-shot
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => run(true)}
            disabled={dryRunning || committing}
            data-testid="pantone-migrate-dry-run-btn"
          >
            {dryRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Dry Run
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadPlan}
            disabled={downloading}
            data-testid="pantone-migrate-export-btn"
          >
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download Full Plan (XLSX)
          </Button>
          <Button
            size="sm"
            onClick={() => setShowCommitConfirm(true)}
            disabled={dryRunning || committing}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="pantone-migrate-commit-btn"
          >
            {committing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Run Migration
          </Button>
        </div>

        {result && (
          <div className="text-xs border rounded-md p-3 bg-zinc-50 space-y-2"
               data-testid="pantone-migrate-result">
            <div className="font-medium text-zinc-700 flex items-center gap-2">
              {resultMode === "dry_run" ? (
                <Badge variant="outline" className="border-blue-300 text-blue-700">DRY RUN</Badge>
              ) : (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border">COMMITTED</Badge>
              )}
              <span>Scanned {result.scanned_untagged} untagged row(s)</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <Stat label="Ready to tag" value={result.migrated} tone="emerald" />
              <Stat label="Invalid candidate" value={result.invalid_candidates}
                    tone={result.invalid_candidates > 0 ? "amber" : "zinc"} />
              <Stat label="Still missing" value={result.still_missing}
                    tone={result.still_missing > 0 ? "amber" : "zinc"} />
            </div>

            {result.sample_migrated?.length > 0 && (
              <details className="text-[11px]" open>
                <summary className="cursor-pointer text-emerald-700 hover:text-emerald-900">
                  Proposed tags (first {result.sample_migrated.length})
                </summary>
                <div className="mt-2 max-h-48 overflow-auto rounded border bg-white">
                  <table className="w-full text-[11px]">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th className="text-left px-2 py-1">RM ID</th>
                        <th className="text-left px-2 py-1">Cat</th>
                        <th className="text-left px-2 py-1">Pantone</th>
                        <th className="text-left px-2 py-1">Source field</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.sample_migrated.map((r, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1 font-mono">{r.rm_id}</td>
                          <td className="px-2 py-1">{r.category}</td>
                          <td className="px-2 py-1 font-mono">{r.pantone_code}</td>
                          <td className="px-2 py-1 text-zinc-600">{r.source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {result.sample_invalid_candidates?.length > 0 && (
              <details className="text-[11px]">
                <summary className="cursor-pointer text-amber-700">
                  Invalid candidates ({result.invalid_candidates}) — candidate found but not in Pantone master
                </summary>
                <div className="mt-2 max-h-48 overflow-auto rounded border bg-white">
                  <table className="w-full text-[11px]">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th className="text-left px-2 py-1">RM ID</th>
                        <th className="text-left px-2 py-1">Cat</th>
                        <th className="text-left px-2 py-1">Candidate</th>
                        <th className="text-left px-2 py-1">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.sample_invalid_candidates.map((r, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1 font-mono">{r.rm_id}</td>
                          <td className="px-2 py-1">{r.category}</td>
                          <td className="px-2 py-1 font-mono">{r.candidate_found}</td>
                          <td className="px-2 py-1 text-zinc-600 truncate max-w-xs">{r.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={showCommitConfirm} onOpenChange={setShowCommitConfirm}>
        <DialogContent data-testid="pantone-migrate-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Commit Pantone Migration?</DialogTitle>
            <DialogDescription>
              This writes <code className="font-mono">category_data.pantone</code> on every MB / PWD row
              where the system found a validated Pantone code (in an alt field
              or description). Rows without a match are NOT touched. Idempotent —
              safe to re-run after fixing data. Run Dry Run first if you haven't.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommitConfirm(false)} disabled={committing}>Cancel</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700"
              onClick={() => run(false)}
              disabled={committing}
              data-testid="pantone-migrate-confirm-btn"
            >
              {committing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Yes, commit migration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const ColourwayBackfillTile = () => {
  const token = useAuthStore((s) => s.token);
  const [dryRunning, setDryRunning] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [downloadingSkipped, setDownloadingSkipped] = useState(false);
  const [result, setResult] = useState(null);
  const [resultMode, setResultMode] = useState(null); // 'dry_run' | 'commit'
  const [showCommitConfirm, setShowCommitConfirm] = useState(false);

  const downloadSkipped = async () => {
    setDownloadingSkipped(true);
    try {
      const res = await axios.get(
        `${API}/colourway/admin/backfill/skipped-export`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      const disp = res.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(disp);
      const outName = m ? m[1] : `colourway_backfill_skipped_${Date.now()}.xlsx`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = outName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const total = res.headers["x-skipped-total"] || "?";
      toast.success(`Downloaded ${total} skipped row(s) — see the Excel for full reason list`);
    } catch (err) {
      let detail = "Download failed";
      try {
        if (err?.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const obj = JSON.parse(text);
          detail = obj.detail || detail;
        } else {
          detail = err?.response?.data?.detail || err.message || detail;
        }
      } catch {
        detail = err.message || detail;
      }
      toast.error(detail, { duration: 8000 });
    } finally {
      setDownloadingSkipped(false);
    }
  };

  const run = async (dryRun) => {
    const setBusy = dryRun ? setDryRunning : setCommitting;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/colourway/admin/backfill?dry_run=${dryRun ? "true" : "false"}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      setResultMode(dryRun ? "dry_run" : "commit");
      if (dryRun) {
        toast.success(`Dry run complete — scanned ${res.data.scanned_parts} part(s)`);
      } else {
        toast.success(`Backfill committed — ${res.data.linked_parts} part(s) tagged`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Backfill failed", { duration: 8000 });
    } finally {
      setBusy(false);
      setShowCommitConfirm(false);
    }
  };

  return (
    <Card data-testid="colourway-backfill-tile">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Palette className="w-5 h-5 text-rose-600" />
              Colourway Backfill (INP / INM)
            </CardTitle>
            <CardDescription className="mt-1 text-xs">
              One-shot DB walker — reads every INP / INMFAB part's rm_bom, mints
              missing Colourant SKUs from MB.pantone, tags MB.colourant_id, and
              denormalises the resolved Colourant SKU list onto each part's
              <code className="font-mono px-1">category_data.colourant_ids</code>.
              Run Dry Run first; review counts + skipped reasons; then Commit.
              Idempotent — safe to re-run.
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-[10px] uppercase border-rose-300 text-rose-700">
            One-shot
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-xs text-zinc-600">
          <span className="font-semibold text-zinc-700">No Excel needed</span> —
          this scans your existing data and writes derived structure.
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => run(true)}
            disabled={dryRunning || committing}
            data-testid="colourway-backfill-dry-run-btn"
          >
            {dryRunning ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Dry Run
          </Button>
          <Button
            size="sm"
            onClick={() => setShowCommitConfirm(true)}
            disabled={dryRunning || committing}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="colourway-backfill-commit-btn"
          >
            {committing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Run Backfill
          </Button>
        </div>

        {result && (
          <div
            className="text-xs border rounded-md p-3 bg-zinc-50 space-y-2"
            data-testid="colourway-backfill-result"
          >
            <div className="font-medium text-zinc-700 flex items-center gap-2">
              {resultMode === "dry_run" ? (
                <Badge variant="outline" className="border-blue-300 text-blue-700">
                  DRY RUN
                </Badge>
              ) : (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border">
                  COMMITTED
                </Badge>
              )}
              <span>Scanned {result.scanned_parts} INP / INM part(s)</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <Stat label="New Colourant SKUs" value={result.created_colourant_skus} tone="emerald" />
              <Stat label="MBs linked" value={result.linked_mbs} tone="emerald" />
              <Stat label="Parts tagged" value={result.linked_parts} tone="emerald" />
              <Stat label="Skipped" value={result.skipped} tone={result.skipped > 0 ? "amber" : "zinc"} />
            </div>

            {result.sample_part_links?.length > 0 && (
              <details className="text-[11px]">
                <summary className="cursor-pointer text-zinc-600 hover:text-zinc-900">
                  Sample part links (first {result.sample_part_links.length})
                </summary>
                <div className="mt-2 max-h-40 overflow-auto rounded border bg-white">
                  <table className="w-full text-[11px]">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th className="text-left px-2 py-1">Part RM</th>
                        <th className="text-left px-2 py-1">Cat</th>
                        <th className="text-left px-2 py-1">Part Name</th>
                        <th className="text-left px-2 py-1">Colourant IDs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.sample_part_links.map((r, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1 font-mono">{r.rm_id}</td>
                          <td className="px-2 py-1">{r.category}</td>
                          <td className="px-2 py-1">{r.part_name}</td>
                          <td className="px-2 py-1 font-mono">{(r.colourant_ids || []).join(", ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {result.sample_created?.length > 0 && (
              <details className="text-[11px]">
                <summary className="cursor-pointer text-zinc-600 hover:text-zinc-900">
                  Sample new Colourant SKUs (first {result.sample_created.length})
                </summary>
                <div className="mt-2 max-h-40 overflow-auto rounded border bg-white">
                  <table className="w-full text-[11px]">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th className="text-left px-2 py-1">Colourant ID</th>
                        <th className="text-left px-2 py-1">Part</th>
                        <th className="text-left px-2 py-1">Polymer</th>
                        <th className="text-left px-2 py-1">Pantone</th>
                        <th className="text-left px-2 py-1">App</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.sample_created.map((r, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1 font-mono">{r.colourant_id}</td>
                          <td className="px-2 py-1">{r.part_id}</td>
                          <td className="px-2 py-1 font-mono">{r.polymer_base}</td>
                          <td className="px-2 py-1">{r.pantone_code}</td>
                          <td className="px-2 py-1">{r.application}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {result.skipped_reasons?.length > 0 && (
              <details className="text-[11px]" data-testid="colourway-backfill-skipped">
                <summary className="cursor-pointer text-amber-700 hover:text-amber-900 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Skipped reasons (preview first {result.skipped_reasons.length}; total {result.skipped})
                </summary>
                <div className="mt-2 flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadSkipped}
                    disabled={downloadingSkipped}
                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    data-testid="colourway-backfill-download-skipped-btn"
                  >
                    {downloadingSkipped ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5 mr-1" />
                    )}
                    Download all {result.skipped} skipped (XLSX)
                  </Button>
                </div>
                <div className="mt-2 max-h-48 overflow-auto rounded border bg-white">
                  <table className="w-full text-[11px]">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th className="text-left px-2 py-1">Part RM</th>
                        <th className="text-left px-2 py-1">Child RM</th>
                        <th className="text-left px-2 py-1">Pantone</th>
                        <th className="text-left px-2 py-1">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.skipped_reasons.map((r, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1 font-mono">{r.rm_id || "—"}</td>
                          <td className="px-2 py-1 font-mono">{r.child_rm_id || "—"}</td>
                          <td className="px-2 py-1">{r.pantone_code || "—"}</td>
                          <td className="px-2 py-1">{r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={showCommitConfirm} onOpenChange={setShowCommitConfirm}>
        <DialogContent data-testid="colourway-backfill-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Commit Colourway Backfill?</DialogTitle>
            <DialogDescription>
              This writes to <code className="font-mono">colourant_skus</code>,
              tags <code className="font-mono">raw_materials.colourant_id</code> on every
              MB / PWD child found, and denormalises
              <code className="font-mono"> category_data.colourant_ids</code> onto every
              INP / INMFAB part. The operation is idempotent — safe to re-run if you
              add Pantone tags to PWD rows later. We strongly recommend running
              Dry Run first.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommitConfirm(false)} disabled={committing}>
              Cancel
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700"
              onClick={() => run(false)}
              disabled={committing}
              data-testid="colourway-backfill-confirm-commit-btn"
            >
              {committing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Yes, commit the backfill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const Stat = ({ label, value, tone }) => {
  const tones = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    zinc: "text-zinc-700 bg-zinc-100 border-zinc-200",
  };
  return (
    <div className={`rounded-md border px-2 py-1 ${tones[tone] || tones.zinc}`}>
      <div className="text-lg font-semibold tabular-nums leading-tight">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wide">{label}</div>
    </div>
  );
};

const DatabaseUpdates = () => {
  return (
    <div className="space-y-6" data-testid="database-updates-page">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <ShieldAlert className="w-7 h-7 text-rose-600" />
            Database Updates
          </h1>
          <p className="text-zinc-500 mt-1 max-w-3xl text-sm">
            One-shot baseline data refreshes — bulk imports that bypass the
            normal operational flows. Use sparingly: every successful row
            writes directly to the underlying collections.
          </p>
        </div>
        <Badge className="bg-rose-100 text-rose-900 border-rose-300 border self-start">
          Master Admin only
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <UpdateTile
          testid="rm-prices-tile"
          title="RM Prices (Vendor × RM)"
          description={
            "HARD RESET per (Vendor, RM): every valid row deletes all prior " +
            "rm_prices_history rows for that pair and inserts exactly one new " +
            "dated entry, forcing Price Master to the uploaded price. Future " +
            "invoice uploads continue to add rows and the 3-month average " +
            "resumes evolving naturally."
          }
          mandatoryColumns={["Vendor ID", "RM ID", "Price"]}
          optionalColumns={["Currency", "Min Order Qty", "Lead Time Days", "Notes"]}
          templateEndpoint="/admin/database-updates/rm-prices/template"
          uploadEndpoint="/admin/database-updates/rm-prices/upload"
          templateFilename="rm_prices_bulk_update_template.xlsx"
        />
        <UpdateTile
          testid="rm-update-tile"
          title="RM Update (any attribute)"
          description={
            "Bulk-update RM attributes — scoped per category. Pick a category " +
            "and the template ships with that category's actual category_data " +
            "columns (Mould Code, Per Unit Weight, Colour, …) alongside the " +
            "universal top-level fields. Empty cells skipped; <<CLEAR>> to erase. " +
            "Identity-locked headers (Category, id, brand_ids, …) and rows whose " +
            "RM doesn't belong to the chosen category are rejected."
          }
          mandatoryColumns={["RM ID"]}
          optionalColumns={[
            "Description",
            "Status",
            "Low Stock Threshold",
            "UoM",
            "category-specific fields (Per Unit Weight, Mould Code, …)",
          ]}
          templateEndpoint="/admin/database-updates/rm-update/template"
          uploadEndpoint="/admin/database-updates/rm-update/upload"
          templateFilename="rm_update_template.xlsx"
          categoryPicker={true}
        />
        <UpdateTile
          testid="rm-delete-tile"
          title="RM Delete (block-if-referenced)"
          description={
            "Bulk-delete RMs from the master. Each row is deleted only when " +
            "NO downstream collection still references the RM AND no branch " +
            "has non-zero stock. Blocked rows return an actionable receipt " +
            "naming the exact collections still using the RM. Zero-stock " +
            "branch_rm_inventory rows are auto-cleaned on success."
          }
          mandatoryColumns={["RM ID"]}
          optionalColumns={[]}
          templateEndpoint="/admin/database-updates/rm-delete/template"
          uploadEndpoint="/admin/database-updates/rm-delete/upload"
          templateFilename="rm_delete_template.xlsx"
        />
        <ColourwayBackfillTile />
        <PantoneMigrationTile />
        <PhaseCStripTile />
        <HealStuckBillsTile />
        <DiagnoseHealRMTile />
        <BackfillRMUnitCostTile />
        <MergeAuditHealthTile />
        <PantoneReconcilerTile />
      </div>
    </div>
  );
};

/* =========================================================================
 * Phase C — Strip Legacy Colour/MB Fields tile (Feb 2026)
 *
 * One-shot admin op that removes `mb`, `colour`, `color` from the
 * `rm_categories.description_columns` array for INP / INM / INMFAB.
 *
 * Flow: Preview (dry-run, safe) → review per-category diff → Execute
 * (live writes). Idempotent on re-run.
 * ========================================================================= */
// --- Heal Stuck Bills Tile -------------------------------------------------
//
// Targets RM Inward bills whose stock was never applied — two known shapes:
//   • status='DRAFT' + zoho_bill_status∈{paid,partially_paid,open}
//   • status='APPROVED' + stock_updated≠true (Feb 2026 — added after a
//     synced-to-Zoho bill didn't increment RM inventory)
//
// Calls POST /api/rm-inward/bills/reconcile-stuck-drafts. The backend is
// idempotent; running this twice in a row is a no-op for already-healed bills.
//
function HealStuckBillsTile() {
  const [preview, setPreview] = useState(null);
  const [executed, setExecuted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [billIdFilter, setBillIdFilter] = useState("");
  const [audit, setAudit] = useState(null);

  const runAudit = async () => {
    if (!billIdFilter.trim()) {
      toast.error("Enter a bill_id to audit");
      return;
    }
    setBusy(true);
    setAudit(null);
    try {
      const r = await axios.get(
        `${API}/rm-inward/bills/${encodeURIComponent(billIdFilter.trim())}/inventory-audit`
      );
      setAudit(r.data);
      const broken = (r.data?.line_audits || []).filter(
        (l) => l.status !== "APPLIED" && l.status !== "FREIGHT"
      );
      if (broken.length === 0) {
        toast.success("All line items applied correctly. Stock loss happened post-bill — see movement history.");
      } else {
        toast.warning(`${broken.length} line item(s) have application issues — see details below.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit failed");
    } finally {
      setBusy(false);
    }
  };

  const runPreview = async () => {
    setBusy(true);
    setExecuted(null);
    try {
      const params = { dry_run: true };
      if (billIdFilter.trim()) params.bill_id = billIdFilter.trim();
      const r = await axios.post(
        `${API}/rm-inward/bills/reconcile-stuck-drafts`,
        null,
        { params }
      );
      setPreview(r.data);
      const n = r.data?.candidate_count || 0;
      if (n === 0) {
        toast.success("All bills are healthy — nothing to heal.");
      } else {
        toast.info(`${n} bill${n === 1 ? "" : "s"} pending stock application.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const runLive = async () => {
    if (!preview || (preview.candidate_count || 0) === 0) {
      toast.info("Nothing to heal — run preview first.");
      return;
    }
    const summary = preview.candidates
      .slice(0, 8)
      .map((c) => `  • ${c.bill_number || c.bill_id} (${c.reason || "stuck"})`)
      .join("\n");
    const ellipsis = preview.candidate_count > 8 ? `\n  …and ${preview.candidate_count - 8} more` : "";
    if (!window.confirm(
      `Apply stock for ${preview.candidate_count} bill${preview.candidate_count === 1 ? "" : "s"}?\n\n` +
      `${summary}${ellipsis}\n\n` +
      `Each bill's line items will be added to branch RM inventory, a purchase ` +
      `entry created, and stock movement logged. Idempotent — safe to re-run.`
    )) return;
    setBusy(true);
    try {
      const params = { dry_run: false };
      if (billIdFilter.trim()) params.bill_id = billIdFilter.trim();
      const r = await axios.post(
        `${API}/rm-inward/bills/reconcile-stuck-drafts`,
        null,
        { params }
      );
      setExecuted(r.data);
      setPreview(null);
      const ok = (r.data?.results || []).filter((x) => x.ok).length;
      const failed = (r.data?.results || []).filter((x) => !x.ok).length;
      if (failed === 0) {
        toast.success(`Healed ${ok} bill${ok === 1 ? "" : "s"}. RM stock updated.`);
      } else {
        toast.warning(`Healed ${ok}, failed ${failed}. Check details below.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Heal failed");
    } finally {
      setBusy(false);
    }
  };

  const renderCandidates = (data) => {
    const items = data?.candidates || [];
    if (items.length === 0) {
      return (
        <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-900">
          ✓ No bills awaiting stock application.
        </div>
      );
    }
    return (
      <div className="space-y-1">
        {items.slice(0, 20).map((c) => (
          <div key={c.bill_id} className="border rounded p-2 bg-white text-xs flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="font-mono font-semibold truncate">{c.bill_number || c.bill_id}</div>
              <div className="text-stone-500 text-[11px] truncate">{c.vendor_name || "—"}</div>
            </div>
            <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider ${
              c.reason === "approved_without_stock"
                ? "bg-amber-100 text-amber-800 border border-amber-300"
                : c.reason === "partial_apply"
                ? "bg-rose-100 text-rose-800 border border-rose-300"
                : "bg-sky-100 text-sky-800 border border-sky-300"
            }`}>
              {c.reason === "approved_without_stock"
                ? "Approved · stock missing"
                : c.reason === "partial_apply"
                ? `Approved · ${(c.missing_rm_ids || []).length} item(s) not booked`
                : "Draft · paid in Zoho"}
            </span>
          </div>
        ))}
        {items.length > 20 && (
          <div className="text-[11px] text-stone-500 px-2">…and {items.length - 20} more.</div>
        )}
      </div>
    );
  };

  const renderResults = (data) => {
    const rows = data?.results || [];
    if (rows.length === 0) return null;
    const ok = rows.filter((r) => r.ok).length;
    const failed = rows.filter((r) => !r.ok);
    return (
      <div className="space-y-2">
        <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-900">
          ✓ Applied stock for {ok} bill{ok === 1 ? "" : "s"}.
        </div>
        {failed.length > 0 && (
          <div className="space-y-1">
            <div className="text-xs font-semibold text-rose-700">Failed ({failed.length}):</div>
            {failed.map((r) => (
              <div key={r.bill_id} className="border border-rose-200 bg-rose-50 rounded p-2 text-[11px]">
                <div className="font-mono">{r.bill_id}</div>
                <div className="text-rose-700">{r.error}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card data-testid="heal-stuck-bills-tile">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              Heal Stuck RM Inward Bills
            </CardTitle>
            <CardDescription className="mt-1 text-xs">
              Re-runs stock application for bills where it was skipped. Catches three
              shapes: <code>DRAFT</code> bills already paid in Zoho, <code>APPROVED</code>{" "}
              bills with <code>stock_updated≠true</code>, and (Feb 2026){" "}
              <code>APPROVED</code> bills where some line items were added after
              initial approval and never got a <code>purchase_entries</code> row.
              The partial-apply heal only books the missing items — never double-counts.
              Idempotent; safe to re-run. Leave the bill ID box empty to heal everything,
              or paste a specific <code>bill_id</code> to target one record.
            </CardDescription>
          </div>
          <Badge className="bg-amber-100 text-amber-900 border-amber-300 border self-start">
            Recovery
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-[11px] text-stone-500 uppercase tracking-wide">
              Bill ID (optional — scope to one bill)
            </Label>
            <input
              type="text"
              value={billIdFilter}
              onChange={(e) => setBillIdFilter(e.target.value)}
              placeholder="Leave empty to scan all bills"
              className="w-full mt-1 px-2 py-1.5 text-xs border border-stone-300 rounded font-mono focus:outline-none focus:ring-2 focus:ring-amber-300"
              data-testid="heal-stuck-bills-id-input"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={runPreview}
            disabled={busy}
            data-testid="heal-stuck-bills-preview-btn"
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
            Preview
          </Button>
          <Button
            size="sm"
            onClick={runLive}
            disabled={busy || !preview || (preview.candidate_count || 0) === 0}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="heal-stuck-bills-run-btn"
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
            Heal {preview?.candidate_count ? `(${preview.candidate_count})` : ""}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={runAudit}
            disabled={busy || !billIdFilter.trim()}
            data-testid="heal-stuck-bills-audit-btn"
          >
            Audit
          </Button>
        </div>

        {preview && renderCandidates(preview)}
        {executed && renderResults(executed)}
        {audit && (
          <div className="space-y-2 text-xs" data-testid="audit-output">
            <div className="bg-stone-100 border rounded p-2">
              <div className="font-semibold">{audit.summary?.bill_number} → {audit.summary?.branch}</div>
              <div className="text-stone-600">
                {audit.summary?.line_item_count} line items · {audit.summary?.purchase_entry_count} purchase entries · {audit.summary?.stock_movement_count} stock movements
              </div>
            </div>
            {(audit.line_audits || []).map((la) => (
              <div key={la.rm_id} className="border rounded p-2 bg-white">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono">{la.rm_id}</div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider ${
                    la.status === "APPLIED"
                      ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                      : la.status === "FREIGHT"
                      ? "bg-stone-100 text-stone-700 border border-stone-300"
                      : "bg-rose-100 text-rose-800 border border-rose-300"
                  }`}>
                    {la.status}
                  </span>
                </div>
                <div className="mt-1 grid grid-cols-3 gap-2 text-[11px] text-stone-600">
                  <div>Expected: <span className="font-mono">{la.expected_qty}</span></div>
                  <div>Applied:  <span className="font-mono">{la.actual_qty ?? "—"}</span></div>
                  <div>Now:      <span className="font-mono">{la.current_stock}</span></div>
                </div>
                {la.post_bill_movements && la.post_bill_movements.length > 1 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-[11px] text-sky-700">
                      Stock movements after this bill ({la.post_bill_movements.length})
                    </summary>
                    <div className="mt-1 space-y-0.5 max-h-40 overflow-auto">
                      {la.post_bill_movements.map((mv) => (
                        <div key={mv.movement_code} className="text-[10px] font-mono border-b border-stone-100 pb-0.5">
                          <span className={
                            mv.movement_type === "PURCHASE" ? "text-emerald-700" :
                            mv.movement_type?.startsWith?.("OUT") || mv.movement_type === "CONSUMPTION" || mv.movement_type === "IBT_OUT" ? "text-rose-700" :
                            "text-stone-600"
                          }>
                            {mv.created_at?.slice(0, 19)} {mv.movement_type} {mv.quantity > 0 ? "+" : ""}{mv.quantity}
                          </span>
                          <span className="text-stone-500"> → {mv.balance_after} · {mv.reference_type}/{mv.reference_id?.slice?.(0, 8)}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PhaseCStripTile() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [executed, setExecuted] = useState(null);

  const runPreview = async () => {
    setBusy(true);
    setExecuted(null);
    try {
      const r = await axios.post(
        `${API}/raw-materials/admin/strip-legacy-colour-fields?dry_run=true`,
      );
      setPreview(r.data);
      const n = (r.data?.impacted_categories || []).length;
      if (n === 0) {
        toast.success("Nothing to strip — categories are already clean.");
      } else {
        toast.info(`${n} categor${n === 1 ? "y" : "ies"} will be cleaned.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const runLive = async () => {
    if (!preview || (preview.impacted_categories || []).length === 0) {
      toast.info("Nothing to strip");
      return;
    }
    const fieldList = preview.impacted_categories
      .map((c) => `${c.category}: ${c.removed_fields.join(", ")}`)
      .join("\n");
    if (!window.confirm(
      `Strip these legacy fields from rm_categories?\n\n${fieldList}\n\n` +
      `This rewrites description_columns. It is safe (reversible by manually ` +
      `re-adding the fields under Tech Ops → RM Categories) and idempotent. Proceed?`
    )) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/raw-materials/admin/strip-legacy-colour-fields?dry_run=false`,
      );
      setExecuted(r.data);
      setPreview(null);
      const n = (r.data?.impacted_categories || []).length;
      toast.success(`Stripped legacy fields from ${n} categor${n === 1 ? "y" : "ies"}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Execute failed");
    } finally {
      setBusy(false);
    }
  };

  const renderDiff = (data) => {
    const impacted = data?.impacted_categories || [];
    if (impacted.length === 0) {
      return (
        <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-900">
          ✓ All categories are already clean — no `mb`, `colour`, or `color` left to strip.
        </div>
      );
    }
    return (
      <div className="space-y-2">
        {impacted.map((c) => (
          <div key={c.category} className="border rounded p-2 bg-white text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono font-bold">{c.category}</span>
              <span className="text-rose-700 text-[11px]">
                will remove: {c.removed_fields.join(", ")}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div>
                <div className="text-stone-500 mb-0.5">Before</div>
                <div className="font-mono break-all">
                  {(c.before || []).join(", ") || "—"}
                </div>
              </div>
              <div>
                <div className="text-stone-500 mb-0.5">After</div>
                <div className="font-mono break-all text-emerald-700">
                  {(c.after || []).join(", ") || "—"}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card data-testid="phase-c-strip-tile">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-rose-600" />
              Strip Legacy Colour / MB Fields (Phase C)
            </CardTitle>
            <CardDescription className="mt-1 text-xs">
              One-shot cleanup. Removes the dead <code>mb</code>, <code>colour</code>,
              {" "}<code>color</code> keys from <code>rm_categories.description_columns</code>
              {" "}for INP / INM / INMFAB. After this runs, the Add RM dialog only
              shows the canonical mould-driven fields. Run once per environment.
              Idempotent.
            </CardDescription>
          </div>
          <Badge className="bg-rose-100 text-rose-900 border-rose-300 border self-start">
            Phase C
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={runPreview}
            disabled={busy}
            data-testid="phase-c-preview-btn"
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
            Preview (dry-run)
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={runLive}
            disabled={busy || !preview || (preview.impacted_categories || []).length === 0}
            data-testid="phase-c-execute-btn"
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShieldAlert className="w-3 h-3 mr-1" />}
            Execute Live Strip
          </Button>
        </div>

        {preview && (
          <div data-testid="phase-c-preview-block">
            <div className="text-[11px] font-semibold text-stone-700 mb-1">
              Preview (no writes yet)
            </div>
            {renderDiff(preview)}
          </div>
        )}

        {executed && (
          <div data-testid="phase-c-executed-block">
            <div className="text-[11px] font-semibold text-emerald-800 mb-1 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Live strip complete
            </div>
            {renderDiff(executed)}
          </div>
        )}

        {!preview && !executed && (
          <div className="text-[11px] text-stone-500">
            Click <b>Preview (dry-run)</b> to see what would change. Nothing is
            written until you click <b>Execute Live Strip</b>.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* =========================================================================
 * Diagnose / Heal RM Tile (Feb 2026)
 *
 * For the "RM is in BOM but doesn't show in Repository / I can't upload
 * inventory for it" class of issues. Calls the two new endpoints:
 *   GET  /api/raw-materials/diagnose/{rm_id}
 *   POST /api/raw-materials/heal/{rm_id}
 * ========================================================================= */
const DiagnoseHealRMTile = () => {
  const token = useAuthStore((s) => s.token);
  const [rmId, setRmId] = useState("");
  const [loading, setLoading] = useState(false);
  const [healing, setHealing] = useState(false);
  const [report, setReport] = useState(null);
  const [healResult, setHealResult] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const diagnose = async () => {
    const id = (rmId || "").trim();
    if (!id) {
      toast.error("Enter an RM ID first (e.g. BS_141)");
      return;
    }
    setLoading(true);
    setReport(null);
    setHealResult(null);
    try {
      const res = await axios.get(
        `${API}/raw-materials/diagnose/${encodeURIComponent(id)}`,
        { headers: authHeaders }
      );
      setReport(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Diagnosis failed");
    } finally {
      setLoading(false);
    }
  };

  const heal = async () => {
    const id = (report?.rm?.rm_id) || (rmId || "").trim();
    if (!id) return;
    setHealing(true);
    setHealResult(null);
    try {
      const res = await axios.post(
        `${API}/raw-materials/heal/${encodeURIComponent(id)}`,
        null,
        { headers: authHeaders }
      );
      setHealResult(res.data);
      toast.success(res.data?.message || "RM healed");
      await diagnose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Heal failed");
    } finally {
      setHealing(false);
    }
  };

  const canHeal =
    report &&
    (report.exists_in_raw_materials === false ||
      (report.hidden_reasons && report.hidden_reasons.length > 0));

  const verdictClass =
    report?.exists_in_raw_materials && report?.visible_in_repository
      ? "bg-emerald-50 border-emerald-300 text-emerald-900"
      : report?.exists_in_raw_materials
      ? "bg-amber-50 border-amber-300 text-amber-900"
      : "bg-rose-50 border-rose-300 text-rose-900";

  return (
    <Card data-testid="diagnose-heal-rm-tile" className="border-stone-300">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-rose-600" />
          Diagnose / Heal Missing RM
        </CardTitle>
        <CardDescription className="text-sm text-stone-600">
          Use this when an RM is referenced in BOM but doesn&rsquo;t show in
          the Repository / refuses inventory uploads. Inspects the RM doc,
          branch inventory and BOM usage, then offers a one-click fix for
          soft-delete (re-activate) or hard-delete (rebuild stub) cases.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-wider font-bold mb-1 block">
              RM ID
            </Label>
            <input
              type="text"
              value={rmId}
              onChange={(e) => setRmId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && diagnose()}
              placeholder="e.g. BS_141"
              data-testid="diagnose-heal-rm-input"
              className="flex h-9 w-full rounded-sm border border-input bg-white px-3 py-2 text-sm font-mono"
            />
          </div>
          <Button
            onClick={diagnose}
            disabled={loading || !rmId.trim()}
            data-testid="diagnose-heal-rm-diagnose-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Diagnose"}
          </Button>
        </div>

        {report && (
          <div
            className={`border rounded-sm p-3 text-sm ${verdictClass}`}
            data-testid="diagnose-heal-rm-verdict"
          >
            <div className="font-semibold mb-1">Verdict</div>
            <div>{report.verdict}</div>

            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
              <span className="text-stone-600">Exists:</span>
              <span>{report.exists_in_raw_materials ? "yes" : "NO"}</span>

              {report.rm && (
                <>
                  <span className="text-stone-600">Status:</span>
                  <span>{report.rm.status ?? "\u2014"}</span>
                  <span className="text-stone-600">is_active:</span>
                  <span>{String(report.rm.is_active)}</span>
                  <span className="text-stone-600">Category:</span>
                  <span>{report.rm.category ?? "\u2014"}</span>
                  <span className="text-stone-600">Description:</span>
                  <span className="truncate">{report.rm.description ?? "\u2014"}</span>
                </>
              )}

              <span className="text-stone-600">Branch inventory rows:</span>
              <span>{report.branch_inventory_count}</span>

              <span className="text-stone-600">BOM references:</span>
              <span>{report.bom_usage?.total ?? 0}</span>
            </div>

            {report.bom_usage?.sample_bidso_sku_ids?.length > 0 && (
              <div className="mt-2 text-xs">
                <span className="text-stone-600">Sample SKUs using this RM: </span>
                <span className="font-mono">
                  {report.bom_usage.sample_bidso_sku_ids.join(", ")}
                </span>
              </div>
            )}

            {report.branch_inventory?.length > 0 && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-stone-700">
                  Branch inventory ({report.branch_inventory.length} rows)
                </summary>
                <table className="mt-2 w-full text-xs font-mono">
                  <thead>
                    <tr className="text-left text-stone-500">
                      <th className="pr-3">Branch</th>
                      <th className="pr-3">Stock</th>
                      <th>Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.branch_inventory.map((b, i) => (
                      <tr key={i}>
                        <td className="pr-3">{b.branch}</td>
                        <td className="pr-3">{b.current_stock}</td>
                        <td>{String(b.is_active)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}

            {canHeal && (
              <div className="mt-3 pt-3 border-t border-current/20">
                <Button
                  onClick={heal}
                  disabled={healing}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="diagnose-heal-rm-heal-btn"
                >
                  {healing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" /> Healing&hellip;
                    </>
                  ) : report.exists_in_raw_materials ? (
                    "Reactivate RM"
                  ) : (
                    "Recreate RM stub"
                  )}
                </Button>
                <p className="text-[11px] mt-2 text-stone-700">
                  {report.exists_in_raw_materials
                    ? "Flips status=ACTIVE + is_active=true. RM becomes immediately visible in Repository and accepts inventory imports."
                    : "Rebuilds a minimal ACTIVE stub (category inferred from prefix). After healing, edit the row in RM Repository to fill in category_data + a proper description."}
                </p>
              </div>
            )}
          </div>
        )}

        {healResult && (
          <div
            className="border border-emerald-300 bg-emerald-50 rounded-sm p-3 text-sm text-emerald-900"
            data-testid="diagnose-heal-rm-heal-result"
          >
            <div className="flex items-center gap-2 font-semibold">
              <CheckCircle2 className="w-4 h-4" /> {healResult.message}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


/* =========================================================================
 * Backfill RM Unit Cost (Moving Weighted Average Cost) — Feb 2026
 *
 * One-time job after the MWAC rollout. Seeds `current_unit_cost` on every
 * `branch_rm_inventory` row that has stock but no cost basis, using the
 * standard waterfall: latest invoice price → vendor_rm_prices.MIN → 0.
 *
 * Endpoint: POST /api/admin/database-updates/branch-rm-inventory/backfill-unit-cost
 * ========================================================================= */
const BackfillRMUnitCostTile = () => {
  const token = useAuthStore((s) => s.token);
  const [loading, setLoading] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [commitResult, setCommitResult] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const run = async (dryRunFlag) => {
    setLoading(true);
    if (dryRunFlag) setDryRun(null);
    else setCommitResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/database-updates/branch-rm-inventory/backfill-unit-cost?dry_run=${dryRunFlag}`,
        null,
        { headers: authHeaders }
      );
      if (dryRunFlag) setDryRun(res.data);
      else {
        setCommitResult(res.data);
        toast.success(res.data?.message || "Backfill complete");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="backfill-rm-unit-cost-tile" className="border-emerald-300">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-600" />
          Backfill RM Unit Cost (MWAC)
        </CardTitle>
        <CardDescription className="text-sm text-stone-600">
          One-time seed for the Moving Weighted Average Cost field on
          existing inventory rows. Uses the waterfall:
          <span className="font-mono mx-1 text-xs">latest invoice → vendor_rm_prices.MIN → 0</span>.
          Going forward, every inward path (manual purchase, no-invoice
          inward, IBT receive, bulk import) maintains MWAC automatically &mdash;
          this only handles existing legacy rows.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button
            onClick={() => run(true)}
            disabled={loading}
            variant="outline"
            data-testid="backfill-rm-unit-cost-dryrun-btn"
          >
            {loading && dryRun === null ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Preview (dry-run)
          </Button>
          <Button
            onClick={() => run(false)}
            disabled={loading || !dryRun}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="backfill-rm-unit-cost-commit-btn"
          >
            {loading && dryRun !== null ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Commit
          </Button>
        </div>
        <p className="text-[11px] text-stone-500">
          You must run the dry-run first to enable Commit. Re-running the
          job is idempotent &mdash; rows that already carry a non-zero unit
          cost are skipped.
        </p>

        {dryRun && (
          <div className="border border-amber-300 bg-amber-50 rounded-sm p-3 text-sm text-amber-900"
               data-testid="backfill-rm-unit-cost-dryrun-result">
            <div className="font-semibold mb-1">Dry-run summary</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
              <span className="text-stone-600">Rows eligible:</span>
              <span>{dryRun.rows_eligible}</span>
              <span className="text-stone-600">Unique RMs:</span>
              <span>{dryRun.unique_rms}</span>
            </div>
            {dryRun.sample?.length > 0 && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-stone-700">
                  Sample (first {dryRun.sample.length} rows)
                </summary>
                <table className="mt-2 w-full text-xs font-mono">
                  <thead>
                    <tr className="text-left text-stone-500">
                      <th className="pr-3">Branch</th>
                      <th className="pr-3">RM</th>
                      <th className="pr-3 text-right">Stock</th>
                      <th className="text-right">Seed Unit Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dryRun.sample.map((r, i) => (
                      <tr key={i}>
                        <td className="pr-3">{r.branch}</td>
                        <td className="pr-3">{r.rm_id}</td>
                        <td className="pr-3 text-right">{r.current_stock}</td>
                        <td className={`text-right ${r.seeded_unit_cost > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                          ₹{r.seeded_unit_cost}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
            <p className="mt-2 text-[11px]">{dryRun.message}</p>
          </div>
        )}

        {commitResult && (
          <div className="border border-emerald-300 bg-emerald-50 rounded-sm p-3 text-sm text-emerald-900"
               data-testid="backfill-rm-unit-cost-commit-result">
            <div className="flex items-center gap-2 font-semibold">
              <CheckCircle2 className="w-4 h-4" /> {commitResult.message}
            </div>
            {commitResult.by_source && (
              <div className="mt-2 grid grid-cols-3 gap-x-4 text-xs font-mono">
                <div>
                  <div className="text-stone-600">Invoice-backed</div>
                  <div className="text-base font-bold">{commitResult.by_source.invoice}</div>
                </div>
                <div>
                  <div className="text-stone-600">Vendor-mapped</div>
                  <div className="text-base font-bold">{commitResult.by_source.vendor_map}</div>
                </div>
                <div>
                  <div className="text-stone-600">No data (₹0)</div>
                  <div className="text-base font-bold text-rose-700">{commitResult.by_source.none}</div>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};



export default DatabaseUpdates;
